import { Alert, Box, Button, Chip, CircularProgress, Sheet, Stack, Typography } from '@mui/joy';
import { useLocation, useNavigate } from 'react-router';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import AddCardRoundedIcon from '@mui/icons-material/AddCardRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';

import { formatMoney } from './adsConstants';


const STATUS_LABELS = {
    active: 'Активен',
    suspended: 'Приостановлен',
    blocked: 'Заблокирован',
};

const STATUS_COLORS = {
    active: 'success',
    suspended: 'warning',
    blocked: 'danger',
};


function Metric({ label, value, hint, color = 'neutral' }) {
    return (
        <Box sx={{ minWidth: 130 }}>
            <Typography level="body-xs" color="neutral">{label}</Typography>
            <Typography level="title-md" sx={{ color: `${color}.700`, fontWeight: 700 }}>
                {value}
            </Typography>
            {hint && (
                <Typography level="body-xs" color="neutral">{hint}</Typography>
            )}
        </Box>
    );
}


/**
 * Плашка рекламного аккаунта над всеми экранами раздела.
 *
 * Показывает свободный остаток, а не просто баланс: часть денег может быть
 * в резерве под дневные бюджеты, и партнёр должен понимать, почему
 * «на счету 5000, а новую кампанию запустить нельзя».
 */
export default function AdsAccountBar({ account, limits, loading, error }) {
    const navigate = useNavigate();
    const location = useLocation();
    // На самой странице биллинга дублирующая кнопка не нужна
    const showTopUp = !location.pathname.startsWith('/business/billing');

    if (loading) {
        return (
            <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md', mb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <CircularProgress size="sm" />
                    <Typography level="body-sm" color="neutral">
                        Загрузка рекламного аккаунта…
                    </Typography>
                </Stack>
            </Sheet>
        );
    }

    if (error) {
        return <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>;
    }

    if (!account) return null;

    const balance = Number(account.balance || 0);
    const hold = Number(account.hold_balance || 0);
    const available = Number(account.available_balance ?? (balance - hold));

    return (
        <Box sx={{ mb: 2 }}>
            {account.status !== 'active' && (
                <Alert
                    color={account.status === 'blocked' ? 'danger' : 'warning'}
                    startDecorator={<LockRoundedIcon />}
                    sx={{ mb: 1.5 }}
                >
                    <Box>
                        <Typography level="title-sm">
                            Рекламный аккаунт {STATUS_LABELS[account.status]?.toLowerCase()}
                        </Typography>
                        <Typography level="body-sm">
                            {account.suspend_reason
                                || 'Создание и запуск кампаний недоступны. Обратитесь в поддержку.'}
                        </Typography>
                    </Box>
                </Alert>
            )}

            {available <= 0 && account.status === 'active' && (
                <Alert
                    color="warning"
                    sx={{ mb: 1.5 }}
                    endDecorator={showTopUp && (
                        <Button
                            size="sm"
                            color="warning"
                            startDecorator={<AddCardRoundedIcon />}
                            onClick={() => navigate('/business/billing')}
                        >
                            Пополнить
                        </Button>
                    )}
                >
                    <Box>
                        <Typography level="title-sm">Недостаточно средств</Typography>
                        <Typography level="body-sm">
                            Свободный остаток — {formatMoney(available)} ₽. Показы не идут,
                            пока счёт не пополнен.
                        </Typography>
                    </Box>
                </Alert>
            )}

            <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md' }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{
                            width: 40, height: 40, borderRadius: 'md',
                            bgcolor: 'primary.softBg', color: 'primary.700',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <AccountBalanceWalletRoundedIcon />
                        </Box>
                        <Box>
                            <Stack direction="row" spacing={0.8} alignItems="center">
                                <Typography level="title-sm">
                                    {account.title || 'Рекламный аккаунт'}
                                </Typography>
                                <Chip
                                    size="sm"
                                    variant="soft"
                                    color={STATUS_COLORS[account.status] || 'neutral'}
                                >
                                    {STATUS_LABELS[account.status] || account.status}
                                </Chip>
                                {account.is_trusted && (
                                    <Chip
                                        size="sm"
                                        variant="soft"
                                        color="success"
                                        startDecorator={<VerifiedRoundedIcon fontSize="small" />}
                                    >
                                        Автомодерация
                                    </Chip>
                                )}
                            </Stack>
                            {limits && (
                                <Typography level="body-xs" color="neutral" sx={{ mt: 0.3 }}>
                                    Мин. ставка {formatMoney(limits.min_cpc)} ₽ за клик ·
                                    мин. дневной бюджет {formatMoney(limits.min_daily_budget)} ₽
                                </Typography>
                            )}
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center">
                        <Metric
                            label="Свободно"
                            value={`${formatMoney(available)} ₽`}
                            color={available > 0 ? 'success' : 'danger'}
                        />
                        <Metric
                            label="В резерве"
                            value={`${formatMoney(hold)} ₽`}
                            hint="под дневные бюджеты"
                        />
                        <Metric
                            label="Потрачено всего"
                            value={`${formatMoney(account.total_spent)} ₽`}
                        />
                        {showTopUp && (
                            <Button
                                size="sm"
                                variant="soft"
                                startDecorator={<AddCardRoundedIcon />}
                                onClick={() => navigate('/partner/ads/billing')}
                            >
                                Пополнить
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </Sheet>
        </Box>
    );
}